import React, { useState } from "react";
import { MapPin, ArrowUpDown, Users } from "lucide-react";
import { Badge } from "../../ui/badge";
import { fmtMoney, SectionCard } from "./dashUtils";

const Stat = ({ label, value, cls = "text-foreground" }) => (
  <div className="bg-muted/40 rounded-lg px-2 py-1.5 text-center">
    <p className={`text-sm font-bold font-['Manrope'] ${cls}`}>{value}</p>
    <p className="text-[9px] text-muted-foreground leading-tight">{label}</p>
  </div>
);

export const LocationCards = ({ locations, selected, onSelect, financialAccess }) => {
  const list = locations || [];
  if (list.length === 0) return null;
  return (
    <SectionCard title="Locations" icon={MapPin} testid="location-cards-v2">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {list.map((loc) => {
          const active = selected === loc.location_id;
          return (
            <button
              key={loc.location_id}
              onClick={() => onSelect(active ? "all" : loc.location_id)}
              className={`rounded-xl border p-3.5 text-left transition-all hover:shadow-sm ${
                active ? "border-blue-500 ring-1 ring-blue-500/30 bg-blue-500/[0.04]" : "border-border hover:border-blue-500/40"
              }`}
              data-testid={`loc-card-${loc.location_id}`}
            >
              <div className="flex items-start justify-between mb-2.5">
                <div className="min-w-0">
                  <p className="font-semibold text-sm text-foreground truncate font-['Manrope']">{loc.name}</p>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {[loc.city, loc.state].filter(Boolean).join(", ") || loc.code || "—"}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {loc.unassigned > 0 && (
                    <Badge className="bg-amber-500/10 text-amber-600 border-0 text-[9px]">{loc.unassigned} unassigned</Badge>
                  )}
                  {active && <Badge className="bg-blue-600 text-white border-0 text-[9px]">Filtered</Badge>}
                </div>
              </div>
              <div className={`grid ${financialAccess ? "grid-cols-3" : "grid-cols-2"} gap-1.5`}>
                <Stat label="Active" value={loc.active_jobs} />
                <Stat label="Completed" value={loc.completed_jobs} cls="text-emerald-600" />
                {financialAccess && <Stat label="Collected" value={fmtMoney(loc.collected)} cls="text-green-600" />}
              </div>
              <div className={`grid ${financialAccess ? "grid-cols-3" : "grid-cols-2"} gap-1.5 mt-1.5`}>
                {financialAccess && <Stat label="Outstanding" value={fmtMoney(loc.outstanding)} cls="text-orange-500" />}
                {financialAccess && <Stat label="Avg Job" value={fmtMoney(loc.avg_job_value)} />}
                <Stat label="Techs" value={loc.technicians} cls="text-blue-500" />
              </div>
            </button>
          );
        })}
      </div>
    </SectionCard>
  );
};

const COLUMNS = [
  { id: "name", label: "Location", money: false },
  { id: "active_jobs", label: "Active" },
  { id: "completed_jobs", label: "Completed" },
  { id: "collected", label: "Collected", money: true, fin: true },
  { id: "outstanding", label: "Outstanding", money: true, fin: true },
  { id: "avg_job_value", label: "Avg Job", money: true, fin: true },
  { id: "tax_collected", label: "Tax", money: true, fin: true },
  { id: "tech_commission", label: "Commission", money: true, fin: true },
  { id: "technicians", label: "Techs" },
];

export const LocationComparison = ({ locations, financialAccess, onSelect }) => {
  const [sortBy, setSortBy] = useState("collected");
  const [dir, setDir] = useState(-1);
  const list = locations || [];
  if (list.length < 2) return null;
  const cols = COLUMNS.filter((c) => financialAccess || !c.fin);
  const sorted = [...list].sort((a, b) => {
    const av = a[sortBy], bv = b[sortBy];
    if (typeof av === "string") return dir * String(av).localeCompare(String(bv));
    return dir * ((av || 0) - (bv || 0));
  });
  const toggleSort = (id) => {
    if (sortBy === id) setDir((d) => -d);
    else { setSortBy(id); setDir(id === "name" ? 1 : -1); }
  };
  return (
    <SectionCard title="Location Performance" icon={ArrowUpDown} testid="location-comparison">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              {cols.map((c) => (
                <th key={c.id}
                  className={`py-2 px-2 text-[11px] font-medium text-muted-foreground cursor-pointer select-none whitespace-nowrap ${c.id === "name" ? "text-left" : "text-right"}`}
                  onClick={() => toggleSort(c.id)}
                  data-testid={`loc-sort-${c.id}`}
                >
                  {c.label}{sortBy === c.id ? (dir === -1 ? " ↓" : " ↑") : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((l) => (
              <tr key={l.location_id} className="border-b border-border/50 hover:bg-muted/30 cursor-pointer" onClick={() => onSelect(l.location_id)} data-testid={`loc-row-${l.location_id}`}>
                {cols.map((c) => (
                  <td key={c.id} className={`py-2 px-2 whitespace-nowrap ${c.id === "name" ? "text-left font-medium text-foreground" : "text-right text-muted-foreground"}`}>
                    {c.id === "name" ? l.name : c.money ? fmtMoney(l[c.id]) : (l[c.id] ?? 0)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-muted-foreground mt-2 flex items-center gap-1"><Users className="w-3 h-3" /> Click a row to filter the dashboard to that location</p>
    </SectionCard>
  );
};
